import { Log } from '../AuxillaryService/Log';
import { Scheduler } from '../Utility/Scheduler';
import { $ } from '../../vendor/jquery';
import { moment } from '../../vendor/moment';

/**
 * Successful Record Response
 *
 * Returns 3 values:
 *
 * 1. {Object} data
 * 2. {IDBObjectStore} objectStore
 * 3. {IDBDatabase} db
 * @typedef {jQuery.Promise} jqpRecordSuccess
 */

/**
 * Successful Request Response
 *
 * Returns 5 values:
 *
 * 1. {*} data
 * 2. {Event} event
 * 3. {IDBRequest} request
 * 4. {IDBObjectStore} objectStore
 * 5. {IDBDatabase|null} db
 * @typedef {jQuery.Promise} jqpRequestSuccess
 */

/**
 * Failure Response
 *
 * Returns 2 values:
 *
 * 1. {string} msg
 * 2. {Event} event
 * @typedef {jQuery.Promise} jqpFailureLocalCache
 */

/**
 * @class
 * @hideconstructor
 */
const LocalCache = {};

/**
 * @constant {string}
 */
LocalCache.DB_NAME = 'REACH_Signage_LocalCache';

/**
 * @constant {string}
 */
LocalCache.DB_OBJECT_STORE = 'localCache';

/**
 * @constant {number}
 */
LocalCache.DB_VERSION = 3;

/**
 * @type {jQuery.Deferred<IDBDatabase>|jQuery.Deferred<string, Event>|null}
 * @private
 */
var _openDatabaseDeferred = null;

/**
 * @type IDBFactory
 * @private
 */
var _indexedDB =
    window.indexedDB ||
    window.webkitIndexedDB ||
    window.mozIndexedDB ||
    window.OIndexedDB ||
    window.msIndexedDB;

/**
 * Try to get the `content` for a given `url`
 * @param {string} cache_id
 * @throws {jqpFailureLocalCache}
 * @returns {jQuery.Promise<*, Object>}
 *      Success `jQuery.Promise[{*} content, {Object} data]`
 */
LocalCache.get = function (cache_id) {
    var jqDeferred = $.Deferred();

    _findRecord(cache_id)
        .done(function (data) {
            if (data === undefined) {
                jqDeferred.reject('Failed to find `' + cache_id + '` in database', null);
            } else {
                jqDeferred.resolve(data.content, data);
            }
        })
        .fail(function (msg, event) {
            Log.Warning(msg, null, __fileline);
            jqDeferred.reject(msg, event);
        });

    return jqDeferred.promise();
};

/**
 * Try to save the `content` for a given `url`
 * @param {string} url
 * @param {*} content
 * @param {string} [cache_id=url]
 * @throws {jqpFailureLocalCache}
 * @returns {jQuery.Promise<number, Object, Event>}
 *      Success `jQuery.Promise[{number} record, {Object} data, {Event} event]`
 */
LocalCache.save = function (url, content, cache_id) {
    cache_id = cache_id || url;

    var jqDeferred = $.Deferred();

    _findRecord(cache_id)
        .done(function (data, objectStore) {
            var newData;

            if (data === undefined) {
                // not in db: insert new data
                newData = {};
            } else {
                // in db: update old data
                newData = data;
            }

            newData.cache_id = cache_id;
            newData.url = url;
            newData.content = content;
            newData.accessed = Date.now();

            _putRequest(newData, objectStore)
                .done(function (record, event) {
                    jqDeferred.resolve(record, newData, event);
                })
                .fail(function (msg, event) {
                    jqDeferred.reject(msg, event);
                });
        })
        .fail(function (msg, event) {
            jqDeferred.reject(msg, event);
        });

    jqDeferred.fail(function (msg) {
        Log.Warning(
            'Failed to save data to the cache: ' + msg,
            'SIGNAGE_LOCAL_CACHE_DB_ERROR',
            __fileline
        );
    });

    return jqDeferred.promise();
};

/**
 * Make record immortal to prevent it from being deleted when purging records
 * (`LocalCache.schedulePurge`)
 * @todo Re-think Cache Purge
 * @param {string} cache_id
 * @throws {jqpFailureLocalCache}
 * @returns {jQuery.Promise<number, Event>}
 *      Success `jQuery.Promise[{number} record, {Event} event]`
 */
LocalCache.makeImmortal = function (cache_id) {
    var jqDeferred = $.Deferred();

    _getRequest('cache_id', cache_id)
        .done(function (data, event, request, objectStore) {
            if (data === undefined) {
                // not in db
                jqDeferred.reject('Failed to find `' + cache_id + '` in database', event);
            } else {
                // in db
                data.isImmortal = true;

                _putRequest(data, objectStore)
                    .done(function (record, event) {
                        jqDeferred.resolve(record, event);
                    })
                    .fail(function (msg, event) {
                        jqDeferred.reject('Failed to make `' + cache_id + '` immortal', event);
                    });
            }
        })
        .fail(function (msg, event) {
            jqDeferred.reject(msg, event);
        });

    return jqDeferred.promise();
};

/**
 * Try to delete the record for a given `url`
 * @param {string} cache_id
 * @throws {jqpFailureLocalCache}
 * @returns {jQuery.Promise<*, Object>}
 *      Success `jQuery.Promise[{*} content, {Object} data]`
 */
LocalCache.delete = function (cache_id) {
    var jqDeferred = $.Deferred();
    var objectStoreJqPromise = _getObjectStore('readwrite');

    objectStoreJqPromise.done(function (objectStore, db) {
        var index = objectStore.index('cache_id');

        var getRequest = index.get(cache_id);

        getRequest.onsuccess = function (event) {
            if (event.target.result) {
                objectStore.delete(event.target.result.id);

                Log.Info(
                    'Deleted 1 database record for cache_id `' +
                        cache_id +
                        '` from `' +
                        LocalCache.DB_NAME +
                        '`',
                    null,
                    __fileline
                );

                jqDeferred.resolve(event, getRequest, objectStore, db);
            } else {
                jqDeferred.reject('Failed to delete from database', event);
            }
        };

        getRequest.onerror = function (event) {
            jqDeferred.reject('Failed to delete from database', event);
        };
    });

    objectStoreJqPromise.fail(function (msg, event) {
        jqDeferred.reject(msg, event);
    });

    return jqDeferred.promise();
};

/**
 * Schedule a recurring purge of inactive records from the database
 * @todo move purge function in to its own method
 * @param {number} [runDuration=15]
 * @param {number} [recordLife=31]
 */
LocalCache.schedulePurge = function (runDuration, recordLife) {
    runDuration = runDuration || 15; //minutes
    recordLife = recordLife || 31; //days

    Scheduler.New()
        .Called('purgeLocalCache')
        .HasAction(function () {
            //purge the database of old records
            _getObjectStore('readwrite').done(function (store) {
                var index = store.index('accessed');
                var upper = moment().subtract(recordLife, 'days').toDate().getTime();
                var range = IDBKeyRange.upperBound(upper, true);
                var count = 0;

                index.openCursor(range).onsuccess = function (event) {
                    var cursor = event.target.result;
                    var isImmortal;

                    if (cursor) {
                        isImmortal =
                            cursor.value.isImmortal !== undefined &&
                            cursor.value.isImmortal === true;

                        if (isImmortal) {
                            // Leave record alone
                            cursor.continue();
                        } else {
                            // Delete record
                            store.delete(cursor.primaryKey);
                            cursor.continue();
                            count++;
                        }
                    } else {
                        if (count > 0) {
                            Log.Info(
                                'Purged ' +
                                    count +
                                    ' database records from `' +
                                    LocalCache.DB_NAME +
                                    '`',
                                null,
                                __fileline
                            );
                        }
                    }
                };
            });
        })
        .RunEvery(runDuration, 'minutes')
        .Starting(moment());
};

/**
 * Try to find IndexedDB Record, and update `accessed` time if found.
 * @param {string} cache_id
 * @throws {jqpFailureLocalCache}
 * @returns {jqpRecordSuccess} `data` is an `Object`
 * containing the record's data
 * @private
 */
var _findRecord = function (cache_id) {
    var jqDeferred = $.Deferred();

    _getRequest('cache_id', cache_id)
        .done(function (data, event, request, objectStore, db) {
            if (data === undefined) {
                // record does not exist
                jqDeferred.resolve(data, objectStore, db);
            } else {
                // update `accessed` time
                data.accessed = Date.now();

                _putRequest(data, objectStore)
                    .done(function () {
                        jqDeferred.resolve(data, objectStore, db);
                    })
                    .fail(function (msg, event) {
                        jqDeferred.reject(msg, event);
                    });
            }

            jqDeferred.resolve(data, objectStore, db);
        })
        .fail(function (msg, event) {
            jqDeferred.reject(msg, event);
        });

    return jqDeferred.promise();
};

/**
 * Try to get the `objectStore` of the IndexedDB
 * @param {string} [mode=readonly]
 * @throws {jqpFailureLocalCache}
 * @returns {jQuery.Promise<IDBObjectStore, IDBDatabase>}
 *      Success `jQuery.Promise[{IDBObjectStore} objectStore, {IDBDatabase} db]`
 * @private
 */
var _getObjectStore = function (mode) {
    mode = mode || 'readonly';

    var jqDeferred = $.Deferred();

    _openDatabase()
        .done(function (db) {
            try {
                var objectStore = db
                    .transaction(LocalCache.DB_OBJECT_STORE, mode)
                    .objectStore(LocalCache.DB_OBJECT_STORE);

                jqDeferred.resolve(objectStore, db);
            } catch (e) {
                jqDeferred.reject('Failed to get LocalCache object store: ' + e);
            }
        })
        .fail(function (msg, event) {
            jqDeferred.reject(msg, event);
        });

    return jqDeferred.promise();
};

/**
 * Try to get data out of the IndexedDB
 * @param {string} index
 * @param {*|IDBKeyRange} key
 * @param {IDBObjectStore} [objectStore]
 * @throws {jqpFailureLocalCache}
 * @returns {jqpRequestSuccess} `data` is an `Object`
 * containing the record's data
 * @private
 */
var _getRequest = function (index, key, objectStore) {
    var jqDeferred = $.Deferred();
    var objectStoreJqPromise;

    if (objectStore instanceof IDBObjectStore) {
        objectStoreJqPromise = $.Deferred();
        objectStoreJqPromise.resolve(objectStore, null);
    } else {
        objectStoreJqPromise = _getObjectStore('readwrite');
    }

    objectStoreJqPromise.done(function (objectStore, db) {
        var urlIndex = objectStore.index(index);
        var getRequest = urlIndex.get(key);

        getRequest.onsuccess = function (event) {
            var data = event.target.result;
            jqDeferred.resolve(data, event, getRequest, objectStore, db);
        };

        getRequest.onerror = function (event) {
            jqDeferred.reject('Failed to retrieve from database', event);
        };
    });

    objectStoreJqPromise.fail(function (msg, event) {
        jqDeferred.reject(msg, event);
    });

    return jqDeferred.promise();
};

/**
 * Try to connect to IndexedDB database
 * @throws {jqpFailureLocalCache}
 * @returns {jQuery.Promise<IDBDatabase>}
 *      Success `jQuery.Promise[{IDBDatabase} db]`
 * @private
 */
var _openDatabase = function () {
    var request;

    var onUpgradeNeeded = function (event) {
        if (event.oldVersion < 1) {
            Log.Info(
                'Create `' + LocalCache.DB_NAME + '` database at version ' + event.newVersion,
                null,
                __fileline
            );
        } else {
            Log.Info(
                'Upgrade `' +
                    LocalCache.DB_NAME +
                    '` database to version ' +
                    event.newVersion +
                    ' from version ' +
                    event.oldVersion,
                null,
                __fileline
            );
        }

        var db = event.target.result;
        var objectStore;

        if (db.objectStoreNames.contains(LocalCache.DB_OBJECT_STORE)) {
            objectStore = event.target.transaction.objectStore(LocalCache.DB_OBJECT_STORE);
        } else {
            objectStore = db.createObjectStore(LocalCache.DB_OBJECT_STORE, {
                keyPath: 'id',
                autoIncrement: true,
            });
        }

        // DB v1
        if (!objectStore.indexNames.contains('url')) {
            objectStore.createIndex('url', 'url', { unique: false }); //DB v3: unique = false
        }
        if (!objectStore.indexNames.contains('accessed')) {
            objectStore.createIndex('accessed', 'accessed', { unique: false });
        }

        // DB v2
        if (!objectStore.indexNames.contains('cache_id')) {
            // <editor-fold desc="Populate `cache_id`">
            var cursorRequest = objectStore.openCursor();

            cursorRequest.onerror = function () {
                Log.Warning('Failed to request cursor', null, __fileline);
            };

            cursorRequest.onsuccess = function (evt) {
                var cursor = evt.target.result;
                var newData;

                if (cursor) {
                    newData = cursor.value;

                    if (newData.cache_id === undefined) {
                        newData.cache_id = newData.url;
                        objectStore.put(newData);
                    }

                    cursor.continue();
                }
            };
            // </editor-fold>

            objectStore.createIndex('cache_id', 'cache_id', { unique: true });
        }

        // DB v3
        if (objectStore.index('url').unique === true) {
            // make `url` index un-unique
            objectStore.deleteIndex('url');
            objectStore.createIndex('url', 'url', { unique: false });
        }
    };

    var onError = function (event) {
        if (event.target.error.name == 'VersionError') {
            // the database is newer than expected (signage was downgraded?)
            Log.Warning(
                'Failed to open database `' +
                    LocalCache.DB_NAME +
                    "`, it's newer than expected.  Trying to downgrade.",
                null,
                __fileline,
                { event: event, message: event.target.error.message }
            );

            // delete existing database
            var deleteRequest = _indexedDB.deleteDatabase(LocalCache.DB_NAME);

            deleteRequest.onerror = function (event) {
                Log.Error(
                    'Failed to downgrade database `' + LocalCache.DB_NAME + '`',
                    null,
                    __fileline,
                    { event: event }
                );
                _openDatabaseDeferred.reject('Failed to downgrade database', event);
            };

            deleteRequest.onsuccess = function () {
                Log.Info(
                    'Downgrade `' +
                        LocalCache.DB_NAME +
                        '` database to version ' +
                        LocalCache.DB_VERSION,
                    null,
                    __fileline
                );

                var secondRequest = _indexedDB.open(LocalCache.DB_NAME, LocalCache.DB_VERSION);

                secondRequest.onupgradeneeded = onUpgradeNeeded;
                secondRequest.onerror = onError;
                secondRequest.onsuccess = onSuccess;
            };
        } else {
            Log.Error('Failed to open database `' + LocalCache.DB_NAME + '`', null, __fileline, {
                event: event,
                message: event.target.error.message,
            });
            _openDatabaseDeferred.reject('Failed to open database', event);
        }
    };

    var onSuccess = function (event) {
        Log.Info(
            'Connection to `' + LocalCache.DB_NAME + '` database successful',
            null,
            __fileline
        );
        var database = event.target.result;
        _openDatabaseDeferred.resolve(database);
    };

    if (_openDatabaseDeferred === null) {
        _openDatabaseDeferred = $.Deferred();

        request = _indexedDB.open(LocalCache.DB_NAME, LocalCache.DB_VERSION);

        request.onupgradeneeded = onUpgradeNeeded;
        request.onerror = onError;
        request.onsuccess = onSuccess;
    }

    return _openDatabaseDeferred.promise();
};

/**
 * Try to put data into the IndexedDB
 * @param {*} data
 * @param {IDBObjectStore} [objectStore]
 * @throws {jqpFailureLocalCache}
 * @returns {jqpRequestSuccess} `data` is an `number` which is
 * the ID of the new record
 * @private
 */
var _putRequest = function (data, objectStore) {
    var jqDeferred = $.Deferred();
    var objectStoreJqPromise;

    if (objectStore instanceof IDBObjectStore) {
        objectStoreJqPromise = $.Deferred();
        objectStoreJqPromise.resolve(objectStore, null);
    } else {
        objectStoreJqPromise = _getObjectStore('readwrite');
    }

    objectStoreJqPromise.done(function (objectStore, db) {
        var putRequest = objectStore.put(data);

        putRequest.onsuccess = function (event) {
            var record = event.target.result;
            jqDeferred.resolve(record, event, putRequest, objectStore, db);
        };

        putRequest.onerror = function (event) {
            jqDeferred.reject('Failed to save to database', event);
        };
    });

    objectStoreJqPromise.fail(function (msg, event) {
        jqDeferred.reject(msg, event);
    });

    return jqDeferred.promise();
};

export { LocalCache };
